import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiHealthResponse } from '../models/api-dto.model';

export type ApiMode = 'demo' | 'live';

const STORAGE_API_MODE_KEY = 'sprinta_api_mode';

@Injectable({
  providedIn: 'root'
})
export class ApiConfigService {

  private apiModeSubject: BehaviorSubject<ApiMode>;
  public apiMode$: Observable<ApiMode>;

  private isApiAvailableSubject = new BehaviorSubject<boolean>(true);
  public isApiAvailable$: Observable<boolean> = this.isApiAvailableSubject.asObservable();

  readonly apiBaseUrl = environment.apiBaseUrl || 'https://api.sprinta.io/v1';

  constructor(private http: HttpClient) {
    this.apiModeSubject = new BehaviorSubject<ApiMode>('live');
    this.apiMode$ = this.apiModeSubject.asObservable();
    this.checkApiHealth().subscribe();
  }

  get currentMode(): ApiMode {
    return 'live';
  }

  get isDemoMode(): boolean {
    return false;
  }

  get isLiveMode(): boolean {
    return true;
  }

  get isApiOnline(): boolean {
    return this.isApiAvailableSubject.value;
  }

  /**
   * Application operates exclusively in Live API mode connected to Sprinta Cloud API.
   */
  setMode(mode: ApiMode): void {
    // Mode is locked to live; kept for backwards compatibility
    this.apiModeSubject.next('live');
    this.checkApiHealth().subscribe();
  }

  /**
   * Unauthenticated deployment health check endpoint: GET /health
   * Does not require Auth0 Bearer token.
   */
  checkApiHealth(): Observable<{ online: boolean; message: string }> {
    return this.http.get<ApiHealthResponse>(`${this.apiBaseUrl}/health`, { observe: 'response' }).pipe(
      timeout(3000),
      map(res => {
        const isOk = res.status >= 200 && res.status < 400;
        this.isApiAvailableSubject.next(isOk);
        return {
          online: isOk,
          message: isOk ? 'Sprinta Cloud API is online.' : 'Sprinta Cloud API returned degraded status.'
        };
      }),
      catchError(err => {
        this.isApiAvailableSubject.next(false);
        return of({
          online: false,
          message: 'Sprinta Cloud API is currently offline or unreachable at ' + this.apiBaseUrl
        });
      })
    );
  }
}
